'use client';

import { useState, type FormEvent } from 'react';

import { SlideOver } from '@/components/admin/SlideOver';
import { notify } from '@/components/super-admin/ui';
import { api } from '@/lib/api';
import { accountError, leaveIfSignedOut } from '@/lib/platform/accounts';
import { parseImpersonation, storeImpersonation } from '@/lib/platform/impersonation';

export function OpenDesk({
  open,
  agencyName,
  slug,
  onClose,
  onStarted,
}: {
  open: boolean;
  agencyName: string;
  slug: string;
  onClose: () => void;
  onStarted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(event: FormEvent) {
    event.preventDefault();
    const trimmed = reason.trim();
    if (trimmed.length < 3 || trimmed.length > 500) {
      setError('Write a reason of at least 3 characters.');
      return;
    }
    setPending(true);
    setError(null);
    try {
      const session = parseImpersonation(await api<unknown>(`/api/platform/impersonation/${slug}`, {
        method: 'POST',
        body: JSON.stringify({ reason: trimmed }),
      }));
      storeImpersonation(session);
      notify('Desk opened for 15 minutes.');
      onStarted();
    } catch (err: unknown) {
      if (!leaveIfSignedOut(err)) setError(accountError(err, 'Could not open this desk.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <SlideOver
      open={open}
      title={`Enter ${agencyName}`}
      onClose={onClose}
      footer={(
        <button type="submit" form="open-desk" disabled={pending} className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50">
          {pending ? 'Opening…' : 'Enter desk'}
        </button>
      )}
    >
      <form id="open-desk" onSubmit={(event) => void start(event)} className="space-y-3">
        <p className="text-sm text-muted">This opens the agency desk as its owner for 15 minutes. The reason is stored in the audit.</p>
        <label className="block text-xs font-medium text-muted">
          Reason
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={500}
            rows={4}
            className="mt-1 w-full rounded-xl border border-line bg-shell px-3 py-2 text-sm text-ink outline-none focus:border-brand"
          />
        </label>
        {error && <p className="text-xs font-semibold text-danger">{error}</p>}
      </form>
    </SlideOver>
  );
}
